/* ============================================================
 * 平台公告 store
 *
 * 10 分钟缓存。公告**不进入**消息中心 / 未读数，独立展示。
 *
 * 575758b（2026-09-02）已读机制：
 *   - `unreadCount` 与消息未读数独立维护（30s 缓存）
 *   - `markRead(ids)` 乐观更新 list[i].isRead=true + unreadCount-=N，
 *     失败静默不回滚（公告是辅助内容，非关键业务）
 *   - markRead 成功后调 `loadUnreadCount(true)` 重拉校准（避免与后端失同步）
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getNoticeList, getNoticeUnreadCount, markNoticeRead } from '@/api/notice'
import type { NoticeItem } from '@/types/notice'

const TTL_MS = 10 * 60 * 1000
const UNREAD_TTL_MS = 30 * 1000

export const useNoticeStore = defineStore('notice', () => {
  const list = ref<NoticeItem[]>([])
  const fetchedAt = ref(0)
  const loading = ref(false)
  const errorMessage = ref('')

  /** 未读公告数（仅登录场景有值；匿名场景保持 0） */
  const unreadCount = ref(0)
  const unreadCountFetchedAt = ref(0)
  const unreadCountLoading = ref(false)

  const isStale = computed(() => Date.now() - fetchedAt.value > TTL_MS)
  const isUnreadStale = computed(() => Date.now() - unreadCountFetchedAt.value > UNREAD_TTL_MS)
  const latest = computed<NoticeItem | null>(() => list.value[0] ?? null)

  async function load(force = false): Promise<void> {
    if (loading.value) return
    if (!force && fetchedAt.value > 0 && !isStale.value) return
    loading.value = true
    errorMessage.value = ''
    try {
      const resp = await getNoticeList()
      list.value = resp.data ?? []
      fetchedAt.value = Date.now()
    } catch (err) {
      errorMessage.value = err instanceof Error ? err.message : '加载公告失败'
    } finally {
      loading.value = false
    }
  }

  /** 加载未读公告数。`force=true` 跳过 30s 缓存。失败静默（不写 errorMessage）。 */
  async function loadUnreadCount(force = false): Promise<void> {
    if (unreadCountLoading.value) return
    if (!force && unreadCountFetchedAt.value > 0 && !isUnreadStale.value) return
    unreadCountLoading.value = true
    try {
      const resp = await getNoticeUnreadCount()
      unreadCount.value = resp.data ?? 0
      unreadCountFetchedAt.value = Date.now()
    } catch {
      // 公告是辅助内容，未读数拉取失败静默（不写 errorMessage，不影响 UI）
    } finally {
      unreadCountLoading.value = false
    }
  }

  /** 标记已读。乐观更新：list[i].isRead=true + unreadCount-=N；
   *  失败不回滚（与 §8.4「markRead 失败不阻塞 UI」对齐）。
   *  成功后重拉 unreadCount 校准。
   */
  async function markRead(ids: number[]): Promise<void> {
    if (ids.length === 0) return
    const idSet = new Set(ids)
    const targets = list.value.filter((n) => idSet.has(n.id) && n.isRead === false)
    if (targets.length === 0) return

    // 乐观更新
    list.value = list.value.map((n) => (idSet.has(n.id) ? { ...n, isRead: true } : n))
    unreadCount.value = Math.max(0, unreadCount.value - targets.length)

    try {
      await markNoticeRead(ids)
      // 成功后重拉校准（避免与后端失同步）
      await loadUnreadCount(true)
    } catch {
      // 失败不回滚（公告是辅助内容，下次进入时后端 isRead 会自然覆盖）
    }
  }

  function reset(): void {
    list.value = []
    fetchedAt.value = 0
    errorMessage.value = ''
    unreadCount.value = 0
    unreadCountFetchedAt.value = 0
  }

  return {
    list,
    latest,
    loading,
    errorMessage,
    load,
    unreadCount,
    unreadCountLoading,
    loadUnreadCount,
    markRead,
    reset,
  }
})
